import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { loadSessionContext, login, parseApiError } from '@saas-kit/lib';
import { ErrorBanner, InputField, SubmitButton } from '@saas-kit/ui';

import { AuthCard } from './AuthCard';
import { loginSchema, type LoginInput } from './schemas';

export function Login(): JSX.Element {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const tokens = await login(values.email, values.password);
      const loaded = await loadSessionContext(tokens.access_token);
      if (!loaded) throw new Error('me_missing');
      // loadSessionContext already resolved the active workspace (last-active,
      // then first). A user with no workspace lands on the no-workspace screen
      // via the RequireTenant guard — no chooser detour needed.
      const next = params.get('next');
      navigate(next ?? '/', { replace: true });
    } catch (e) {
      setServerError(parseApiError(e).i18nKey);
    }
  });

  const errs = form.formState.errors;
  return (
    <AuthCard title={t('login.title')} subtitle={t('login.subtitle')}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {serverError ? <ErrorBanner>{t(serverError)}</ErrorBanner> : null}
        <InputField
          label={t('fields.email')}
          type="email"
          autoComplete="email"
          placeholder={t('fields.email_placeholder')}
          {...form.register('email')}
          error={errs.email ? t('validation.email_invalid') : undefined}
        />
        <InputField
          label={t('fields.password')}
          type="password"
          autoComplete="current-password"
          placeholder={t('fields.password_placeholder')}
          {...form.register('password')}
        />
        <SubmitButton
          loading={form.formState.isSubmitting}
          label={t('login.submit')}
          loadingLabel={t('login.submitting')}
        />
      </form>
    </AuthCard>
  );
}
